import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail, Plus, Trash2, Send, Loader2, Clock, CheckCircle2,
  XCircle, Bell, Settings, History, ChevronDown, ChevronUp, Save,
} from "lucide-react";

const NOTIFICATION_TYPE_META = {
  hourly_delivery_report: {
    icon: Mail,
    color: "bg-blue-100 text-blue-700",
    description: "Sends a summary of delivered orders and rider-wise breakdown for the previous hour window.",
  },
  invoice_dues_followup: {
    icon: Bell,
    color: "bg-amber-100 text-amber-700",
    description: "Daily email with pending invoice dues from Pharmeasy. Includes table with aging and attached PDFs.",
  },
  invoice_generation_reminder: {
    icon: Clock,
    color: "bg-red-100 text-red-700",
    description: "Reminder when completed billing weeks have no invoice generated. Lists pending weeks with delivery counts.",
  },
  escalation_new_upload: {
    icon: Bell,
    color: "bg-emerald-100 text-emerald-700",
    description: "Triggered when a new escalation file is uploaded.",
  },
  escalation_status_open: {
    icon: Bell,
    color: "bg-sky-100 text-sky-700",
    description: "Triggered when an escalation is set to Open.",
  },
  escalation_status_in_progress: {
    icon: Clock,
    color: "bg-orange-100 text-orange-700",
    description: "Triggered when an escalation moves to In Progress.",
  },
  escalation_status_resolved: {
    icon: CheckCircle2,
    color: "bg-green-100 text-green-700",
    description: "Triggered when an escalation is marked Resolved.",
  },
  escalation_status_closed: {
    icon: XCircle,
    color: "bg-slate-100 text-slate-700",
    description: "Triggered when an escalation is Closed.",
  },
  escalation_status_reopened: {
    icon: Bell,
    color: "bg-red-100 text-red-700",
    description: "Triggered when a closed escalation is Reopened.",
  },
  escalation_daily_summary: {
    icon: Clock,
    color: "bg-teal-100 text-teal-700",
    description: "Daily morning email with Open and In Progress escalation counts and lists. Runs at 9:15 AM IST.",
  },
};

const PharmeasyNotifications = ({ user, onLogout }) => {
  const [types, setTypes] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [triggering, setTriggering] = useState({});
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState("configs"); // configs | logs
  const [expandedConfig, setExpandedConfig] = useState(null);

  // Hourly report send dialog
  const [showHourlySendDialog, setShowHourlySendDialog] = useState(false);
  const [hourlyOverrideDate, setHourlyOverrideDate] = useState("");
  const [hourlyOverrideHourStart, setHourlyOverrideHourStart] = useState("");
  const [hourlyOverrideHourEnd, setHourlyOverrideHourEnd] = useState("");

  // New config form state
  const [newConfig, setNewConfig] = useState({
    notification_type: "",
    recipient_emails: [],
    cc_emails: [],
    bcc_emails: [],
    schedule_minute: 30,
    schedule_hour: null,
    subject_template: "",
    from_name: "EdgeOS Support Team",
    footer_text: "Regards\nEdgeOS Support Team",
    enabled: true,
  });
  const [newEmail, setNewEmail] = useState("");
  const [newCcEmail, setNewCcEmail] = useState("");
  const [newBccEmail, setNewBccEmail] = useState("");

  // Edit state per config
  const [editState, setEditState] = useState({});
  const [editEmailInput, setEditEmailInput] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, configsRes, logsRes] = await Promise.all([
        axios.get(`${API}/pharmeasy-notifications/types`),
        axios.get(`${API}/pharmeasy-notifications/configs`),
        axios.get(`${API}/pharmeasy-notifications/logs?limit=30`),
      ]);
      setTypes(typesRes.data);
      setConfigs(configsRes.data.configs || []);
      setLogs(logsRes.data.logs || []);
    } catch (e) {
      toast.error("Failed to load notification configs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddEmail = (list, setter, email, emailSetter) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) return toast.error("Enter a valid email");
    if (list.includes(trimmed)) return toast.error("Email already added");
    setter([...list, trimmed]);
    emailSetter("");
  };

  const handleRemoveEmail = (list, setter, email) => {
    setter(list.filter((e) => e !== email));
  };

  const handleCreateConfig = async () => {
    if (!newConfig.notification_type) return toast.error("Select a notification type");
    if (!newConfig.recipient_emails.length) return toast.error("Add at least one recipient email");

    try {
      await axios.post(`${API}/pharmeasy-notifications/configs`, newConfig);
      toast.success("Notification config created");
      setShowAddForm(false);
      setNewConfig({
        notification_type: "", recipient_emails: [], cc_emails: [], bcc_emails: [],
        schedule_minute: 30, schedule_hour: null,
        subject_template: "", from_name: "EdgeOS Support Team",
        footer_text: "Regards\nEdgeOS Support Team", enabled: true,
      });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create config");
    }
  };

  const handleUpdateConfig = async (ntype) => {
    const edit = editState[ntype];
    if (!edit) return;

    setSaving((p) => ({ ...p, [ntype]: true }));
    try {
      await axios.put(`${API}/pharmeasy-notifications/configs/${ntype}`, edit);
      toast.success("Configuration updated");
      setEditState((p) => { const n = { ...p }; delete n[ntype]; return n; });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving((p) => ({ ...p, [ntype]: false }));
    }
  };

  const handleToggle = async (ntype, currentEnabled) => {
    try {
      await axios.put(`${API}/pharmeasy-notifications/configs/${ntype}`, { enabled: !currentEnabled });
      toast.success(`Notification ${!currentEnabled ? "enabled" : "disabled"}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (ntype) => {
    if (!window.confirm("Delete this notification config?")) return;
    try {
      await axios.delete(`${API}/pharmeasy-notifications/configs/${ntype}`);
      toast.success("Configuration deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleTrigger = async (ntype, overrideParams = {}) => {
    setTriggering((p) => ({ ...p, [ntype]: true }));
    try {
      const params = new URLSearchParams();
      if (overrideParams.override_date) params.append("override_date", overrideParams.override_date);
      if (overrideParams.override_hour_start !== undefined) params.append("override_hour_start", overrideParams.override_hour_start);
      if (overrideParams.override_hour_end !== undefined) params.append("override_hour_end", overrideParams.override_hour_end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.post(`${API}/pharmeasy-notifications/trigger/${ntype}${qs}`);
      if (res.data.success) {
        toast.success(`Email sent! Delivered: ${res.data.stats?.total_delivered || 0}, Pending: ${res.data.stats?.total_pending || 0}`);
      } else {
        toast.error(res.data.message || "Failed to send");
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to trigger");
    } finally {
      setTriggering((p) => ({ ...p, [ntype]: false }));
    }
  };

  const handleHourlySendWithOverride = () => {
    handleTrigger("hourly_delivery_report", {
      override_date: hourlyOverrideDate,
      override_hour_start: hourlyOverrideHourStart,
      override_hour_end: hourlyOverrideHourEnd,
    });
    setShowHourlySendDialog(false);
  };

  const initEditState = (config) => {
    setEditState((p) => ({
      ...p,
      [config.notification_type]: {
        recipient_emails: [...(config.recipient_emails || [])],
        cc_emails: [...(config.cc_emails || [])],
        bcc_emails: [...(config.bcc_emails || [])],
        schedule_minute: config.schedule_minute,
        schedule_hour: config.schedule_hour ?? null,
        subject_template: config.subject_template,
        from_name: config.from_name,
        footer_text: config.footer_text,
      },
    }));
  };

  const existingTypes = configs.map((c) => c.notification_type);

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-900">Notification Emails</h1>
            <p className="text-sm text-slate-500 mt-0.5">Configure automated email reports for Pharmeasy operations</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant={activeTab === "configs" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("configs")}
              data-testid="tab-configs"
            >
              <Settings className="w-3.5 h-3.5 mr-1" /> Configs
            </Button>
            <Button
              variant={activeTab === "logs" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("logs")}
              data-testid="tab-logs"
            >
              <History className="w-3.5 h-3.5 mr-1" /> Logs
            </Button>
          </div>
        </div>

        {activeTab === "configs" && (
          <>
            {/* Existing configs */}
            {configs.map((config) => {
              const meta = NOTIFICATION_TYPE_META[config.notification_type] || {};
              const Icon = meta.icon || Bell;
              const isExpanded = expandedConfig === config.notification_type;
              const edit = editState[config.notification_type];

              return (
                <Card key={config.notification_type} className="border-slate-200" data-testid={`config-${config.notification_type}`}>
                  <CardHeader className="pb-2 cursor-pointer" onClick={() => {
                    setExpandedConfig(isExpanded ? null : config.notification_type);
                    if (!isExpanded && !edit) initEditState(config);
                  }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md ${meta.color || "bg-slate-100 text-slate-700"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <CardTitle className="text-sm font-semibold">
                            {types.find((t) => t.value === config.notification_type)?.label || config.notification_type}
                          </CardTitle>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {config.schedule_hour != null 
                              ? `Daily at ${String(config.schedule_hour).padStart(2, "0")}:${String(config.schedule_minute).padStart(2, "0")} IST`
                              : `Triggers at minute :${String(config.schedule_minute).padStart(2, "0")} of every hour`}
                            {config.last_sent_at && (
                              <span className="ml-2">
                                | Last sent: {new Date(config.last_sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC" })}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={config.enabled}
                          onCheckedChange={() => handleToggle(config.notification_type, config.enabled)}
                          data-testid={`toggle-${config.notification_type}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (config.notification_type === "hourly_delivery_report") {
                              setShowHourlySendDialog(true);
                            } else {
                              handleTrigger(config.notification_type);
                            }
                          }}
                          disabled={triggering[config.notification_type]}
                          data-testid={`trigger-${config.notification_type}`}
                          className="h-7 px-2 text-xs"
                        >
                          {triggering[config.notification_type] ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><Send className="w-3 h-3 mr-1" /> Send Now</>
                          )}
                        </Button>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                  </CardHeader>

                  {isExpanded && edit && (
                    <CardContent className="pt-0 space-y-4">
                      <div className="border-t pt-3 space-y-3">
                        {/* Recipients */}
                        <div>
                          <label className="text-xs font-medium text-slate-600">Recipient Emails</label>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {edit.recipient_emails.map((email) => (
                              <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1">
                                {email}
                                <button onClick={() => {
                                  setEditState((p) => ({
                                    ...p,
                                    [config.notification_type]: {
                                      ...p[config.notification_type],
                                      recipient_emails: p[config.notification_type].recipient_emails.filter((e) => e !== email),
                                    },
                                  }));
                                }}>
                                  <XCircle className="w-3 h-3 text-slate-400 hover:text-red-500" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          <div className="flex gap-1.5 mt-1.5">
                            <Input
                              type="email"
                              placeholder="Add email and press Enter"
                              className="h-7 text-xs"
                              value={editEmailInput[config.notification_type] || ""}
                              onChange={(e) => setEditEmailInput((p) => ({ ...p, [config.notification_type]: e.target.value }))}
                              data-testid={`edit-email-input-${config.notification_type}`}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (editEmailInput[config.notification_type] || "").trim().toLowerCase();
                                  if (!val || !val.includes("@")) return toast.error("Enter a valid email");
                                  if (edit.recipient_emails.includes(val)) return toast.error("Already added");
                                  setEditState((p) => ({
                                    ...p,
                                    [config.notification_type]: {
                                      ...p[config.notification_type],
                                      recipient_emails: [...p[config.notification_type].recipient_emails, val],
                                    },
                                  }));
                                  setEditEmailInput((p) => ({ ...p, [config.notification_type]: "" }));
                                }
                              }}
                            />
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => {
                              const val = (editEmailInput[config.notification_type] || "").trim().toLowerCase();
                              if (!val || !val.includes("@")) return toast.error("Enter a valid email");
                              if (edit.recipient_emails.includes(val)) return toast.error("Already added");
                              setEditState((p) => ({
                                ...p,
                                [config.notification_type]: {
                                  ...p[config.notification_type],
                                  recipient_emails: [...p[config.notification_type].recipient_emails, val],
                                },
                              }));
                              setEditEmailInput((p) => ({ ...p, [config.notification_type]: "" }));
                            }}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* CC Emails */}
                        <div>
                          <label className="text-xs font-medium text-slate-600">CC Emails</label>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {(edit.cc_emails || []).map((email) => (
                              <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1 bg-blue-50 text-blue-700">
                                {email}
                                <button onClick={() => {
                                  setEditState((p) => ({
                                    ...p,
                                    [config.notification_type]: {
                                      ...p[config.notification_type],
                                      cc_emails: p[config.notification_type].cc_emails.filter((e) => e !== email),
                                    },
                                  }));
                                }}>
                                  <XCircle className="w-3 h-3 text-blue-400 hover:text-red-500" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          <div className="flex gap-1.5 mt-1.5">
                            <Input
                              type="email"
                              placeholder="Add CC email"
                              className="h-7 text-xs"
                              value={editEmailInput[`${config.notification_type}_cc`] || ""}
                              onChange={(e) => setEditEmailInput((p) => ({ ...p, [`${config.notification_type}_cc`]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (editEmailInput[`${config.notification_type}_cc`] || "").trim().toLowerCase();
                                  if (!val || !val.includes("@")) return toast.error("Enter a valid email");
                                  setEditState((p) => ({
                                    ...p,
                                    [config.notification_type]: {
                                      ...p[config.notification_type],
                                      cc_emails: [...(p[config.notification_type].cc_emails || []), val],
                                    },
                                  }));
                                  setEditEmailInput((p) => ({ ...p, [`${config.notification_type}_cc`]: "" }));
                                }
                              }}
                            />
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => {
                              const val = (editEmailInput[`${config.notification_type}_cc`] || "").trim().toLowerCase();
                              if (!val || !val.includes("@")) return toast.error("Enter a valid email");
                              setEditState((p) => ({
                                ...p,
                                [config.notification_type]: {
                                  ...p[config.notification_type],
                                  cc_emails: [...(p[config.notification_type].cc_emails || []), val],
                                },
                              }));
                              setEditEmailInput((p) => ({ ...p, [`${config.notification_type}_cc`]: "" }));
                            }}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* BCC Emails */}
                        <div>
                          <label className="text-xs font-medium text-slate-600">BCC Emails</label>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {(edit.bcc_emails || []).map((email) => (
                              <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1 bg-slate-100 text-slate-600">
                                {email}
                                <button onClick={() => {
                                  setEditState((p) => ({
                                    ...p,
                                    [config.notification_type]: {
                                      ...p[config.notification_type],
                                      bcc_emails: p[config.notification_type].bcc_emails.filter((e) => e !== email),
                                    },
                                  }));
                                }}>
                                  <XCircle className="w-3 h-3 text-slate-400 hover:text-red-500" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          <div className="flex gap-1.5 mt-1.5">
                            <Input
                              type="email"
                              placeholder="Add BCC email"
                              className="h-7 text-xs"
                              value={editEmailInput[`${config.notification_type}_bcc`] || ""}
                              onChange={(e) => setEditEmailInput((p) => ({ ...p, [`${config.notification_type}_bcc`]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const val = (editEmailInput[`${config.notification_type}_bcc`] || "").trim().toLowerCase();
                                  if (!val || !val.includes("@")) return toast.error("Enter a valid email");
                                  setEditState((p) => ({
                                    ...p,
                                    [config.notification_type]: {
                                      ...p[config.notification_type],
                                      bcc_emails: [...(p[config.notification_type].bcc_emails || []), val],
                                    },
                                  }));
                                  setEditEmailInput((p) => ({ ...p, [`${config.notification_type}_bcc`]: "" }));
                                }
                              }}
                            />
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => {
                              const val = (editEmailInput[`${config.notification_type}_bcc`] || "").trim().toLowerCase();
                              if (!val || !val.includes("@")) return toast.error("Enter a valid email");
                              setEditState((p) => ({
                                ...p,
                                [config.notification_type]: {
                                  ...p[config.notification_type],
                                  bcc_emails: [...(p[config.notification_type].bcc_emails || []), val],
                                },
                              }));
                              setEditEmailInput((p) => ({ ...p, [`${config.notification_type}_bcc`]: "" }));
                            }}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Schedule */}
                        <div className="flex gap-4">
                          <div>
                            <label className="text-xs font-medium text-slate-600">Trigger Minute</label>
                            <Input
                            type="number"
                            min={0}
                            max={59}
                            value={edit.schedule_minute}
                            onChange={(e) => setEditState((p) => ({
                              ...p,
                              [config.notification_type]: { ...p[config.notification_type], schedule_minute: parseInt(e.target.value) || 0 },
                            }))}
                            className="h-7 text-xs w-20 mt-1"
                            data-testid={`edit-minute-${config.notification_type}`}
                          />
                          <p className="text-xs text-slate-400 mt-0.5">0-59</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600">Hour (IST, daily only)</label>
                            <Input
                              type="number"
                              min={0}
                              max={23}
                              value={edit.schedule_hour ?? ""}
                              onChange={(e) => setEditState((p) => ({
                                ...p,
                                [config.notification_type]: { ...p[config.notification_type], schedule_hour: e.target.value === "" ? null : parseInt(e.target.value) },
                              }))}
                              className="h-7 text-xs w-20 mt-1"
                              placeholder="--"
                              data-testid={`edit-hour-${config.notification_type}`}
                            />
                            <p className="text-xs text-slate-400 mt-0.5">Empty = hourly</p>
                          </div>
                        </div>

                        {/* Subject Template */}
                        <div>
                          <label className="text-xs font-medium text-slate-600">Email Subject Template</label>
                          <Input
                            value={edit.subject_template}
                            onChange={(e) => setEditState((p) => ({
                              ...p,
                              [config.notification_type]: { ...p[config.notification_type], subject_template: e.target.value },
                            }))}
                            className="h-7 text-xs mt-1"
                            placeholder="Use {{hub_name}}, {{time_range}}, {{date}}"
                            data-testid={`edit-subject-${config.notification_type}`}
                          />
                        </div>

                        {/* From Name */}
                        <div>
                          <label className="text-xs font-medium text-slate-600">From Name</label>
                          <Input
                            value={edit.from_name}
                            onChange={(e) => setEditState((p) => ({
                              ...p,
                              [config.notification_type]: { ...p[config.notification_type], from_name: e.target.value },
                            }))}
                            className="h-7 text-xs mt-1 w-64"
                            data-testid={`edit-from-${config.notification_type}`}
                          />
                        </div>

                        {/* Footer Text */}
                        <div>
                          <label className="text-xs font-medium text-slate-600">Footer Text</label>
                          <textarea
                            value={edit.footer_text}
                            onChange={(e) => setEditState((p) => ({
                              ...p,
                              [config.notification_type]: { ...p[config.notification_type], footer_text: e.target.value },
                            }))}
                            className="w-full h-16 text-xs mt-1 border rounded-md p-2 resize-none"
                            data-testid={`edit-footer-${config.notification_type}`}
                          />
                        </div>

                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateConfig(config.notification_type)}
                            disabled={saving[config.notification_type]}
                            data-testid={`save-config-${config.notification_type}`}
                            className="h-7 text-xs"
                          >
                            {saving[config.notification_type] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                            Save Changes
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(config.notification_type)}
                            className="h-7 text-xs"
                            data-testid={`delete-config-${config.notification_type}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {/* Add New Config */}
            {!showAddForm ? (
              <Button
                variant="outline"
                onClick={() => setShowAddForm(true)}
                className="w-full border-dashed"
                data-testid="add-notification-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Notification
              </Button>
            ) : (
              <Card className="border-slate-200 border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">New Notification Config</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Type Selector */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">Notification Type</label>
                    <select
                      className="w-full h-8 text-xs border rounded-md px-2 mt-1"
                      value={newConfig.notification_type}
                      onChange={(e) => {
                        const t = types.find((t) => t.value === e.target.value);
                        setNewConfig((p) => ({
                          ...p,
                          notification_type: e.target.value,
                          subject_template: t?.default_subject || "",
                        }));
                      }}
                      data-testid="new-type-select"
                    >
                      <option value="">Select type...</option>
                      {types.filter((t) => !existingTypes.includes(t.value)).map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Recipients */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">Recipient Emails</label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {newConfig.recipient_emails.map((email) => (
                        <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1">
                          {email}
                          <button onClick={() => handleRemoveEmail(newConfig.recipient_emails, (v) => setNewConfig((p) => ({ ...p, recipient_emails: v })), email)}>
                            <XCircle className="w-3 h-3 text-slate-400 hover:text-red-500" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-1.5 mt-1.5">
                      <Input
                        type="email"
                        placeholder="Enter email and press Enter"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="h-7 text-xs"
                        data-testid="new-email-input"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddEmail(newConfig.recipient_emails, (v) => setNewConfig((p) => ({ ...p, recipient_emails: v })), newEmail, setNewEmail);
                          }
                        }}
                      />
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAddEmail(newConfig.recipient_emails, (v) => setNewConfig((p) => ({ ...p, recipient_emails: v })), newEmail, setNewEmail)}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* CC Emails */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">CC Emails</label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {newConfig.cc_emails.map((email) => (
                        <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1 bg-blue-50 text-blue-700">
                          {email}
                          <button onClick={() => setNewConfig((p) => ({ ...p, cc_emails: p.cc_emails.filter((e) => e !== email) }))}>
                            <XCircle className="w-3 h-3 text-blue-400 hover:text-red-500" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-1.5 mt-1.5">
                      <Input
                        type="email"
                        placeholder="Add CC email"
                        value={newCcEmail}
                        onChange={(e) => setNewCcEmail(e.target.value)}
                        className="h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddEmail(newConfig.cc_emails, (v) => setNewConfig((p) => ({ ...p, cc_emails: v })), newCcEmail, setNewCcEmail);
                          }
                        }}
                      />
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAddEmail(newConfig.cc_emails, (v) => setNewConfig((p) => ({ ...p, cc_emails: v })), newCcEmail, setNewCcEmail)}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* BCC Emails */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">BCC Emails</label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {newConfig.bcc_emails.map((email) => (
                        <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1 bg-slate-100 text-slate-600">
                          {email}
                          <button onClick={() => setNewConfig((p) => ({ ...p, bcc_emails: p.bcc_emails.filter((e) => e !== email) }))}>
                            <XCircle className="w-3 h-3 text-slate-400 hover:text-red-500" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-1.5 mt-1.5">
                      <Input
                        type="email"
                        placeholder="Add BCC email"
                        value={newBccEmail}
                        onChange={(e) => setNewBccEmail(e.target.value)}
                        className="h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddEmail(newConfig.bcc_emails, (v) => setNewConfig((p) => ({ ...p, bcc_emails: v })), newBccEmail, setNewBccEmail);
                          }
                        }}
                      />
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAddEmail(newConfig.bcc_emails, (v) => setNewConfig((p) => ({ ...p, bcc_emails: v })), newBccEmail, setNewBccEmail)}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="flex gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Trigger Minute</label>
                      <Input
                        type="number" min={0} max={59}
                        value={newConfig.schedule_minute}
                        onChange={(e) => setNewConfig((p) => ({ ...p, schedule_minute: parseInt(e.target.value) || 0 }))}
                        className="h-7 text-xs w-20 mt-1"
                        data-testid="new-minute-input"
                      />
                      <p className="text-xs text-slate-400 mt-0.5">0-59</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Hour (IST, daily only)</label>
                      <Input
                        type="number" min={0} max={23}
                        value={newConfig.schedule_hour ?? ""}
                        onChange={(e) => setNewConfig((p) => ({ ...p, schedule_hour: e.target.value === "" ? null : parseInt(e.target.value) }))}
                        className="h-7 text-xs w-20 mt-1"
                        placeholder="--"
                        data-testid="new-hour-input"
                      />
                      <p className="text-xs text-slate-400 mt-0.5">Empty = hourly</p>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">Subject Template</label>
                    <Input
                      value={newConfig.subject_template}
                      onChange={(e) => setNewConfig((p) => ({ ...p, subject_template: e.target.value }))}
                      className="h-7 text-xs mt-1"
                      placeholder="Use {{hub_name}}, {{time_range}}, {{date}}"
                      data-testid="new-subject-input"
                    />
                  </div>

                  {/* From Name */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">From Name</label>
                    <Input
                      value={newConfig.from_name}
                      onChange={(e) => setNewConfig((p) => ({ ...p, from_name: e.target.value }))}
                      className="h-7 text-xs mt-1 w-64"
                      data-testid="new-from-input"
                    />
                  </div>

                  {/* Footer */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">Footer Text</label>
                    <textarea
                      value={newConfig.footer_text}
                      onChange={(e) => setNewConfig((p) => ({ ...p, footer_text: e.target.value }))}
                      className="w-full h-16 text-xs mt-1 border rounded-md p-2 resize-none"
                      data-testid="new-footer-input"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateConfig} className="h-7 text-xs" data-testid="create-config-btn">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Create
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)} className="h-7 text-xs">
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Logs Tab */}
        {activeTab === "logs" && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Email Send Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No email logs yet</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg text-xs">
                      <div className={`p-1 rounded ${log.success ? "bg-emerald-100" : "bg-red-100"}`}>
                        {log.success ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-red-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{log.subject || log.notification_type}</p>
                        <p className="text-slate-400 mt-0.5">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                          <span className="mx-1">|</span>
                          {log.triggered_by}
                          {log.stats && (
                            <>
                              <span className="mx-1">|</span>
                              Delivered: {log.stats.total_delivered}, OFD: {log.stats.total_ofd}, Window: {log.stats.window}
                            </>
                          )}
                        </p>
                        {log.error && <p className="text-red-500 mt-0.5">{log.error}</p>}
                        {log.recipients && (
                          <p className="text-slate-400 mt-0.5">To: {log.recipients.join(", ")}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Hourly Delivery Report Send Dialog */}
      <Dialog open={showHourlySendDialog} onOpenChange={setShowHourlySendDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="w-4 h-4 text-teal-600" /> Send Hourly Delivery Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Date</Label>
              <Input
                type="date"
                value={hourlyOverrideDate}
                onChange={(e) => setHourlyOverrideDate(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Hour Start (IST)</Label>
                <Select value={hourlyOverrideHourStart} onValueChange={setHourlyOverrideHourStart}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, "0")}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Hour End (IST)</Label>
                <Select value={hourlyOverrideHourEnd} onValueChange={setHourlyOverrideHourEnd}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="To" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, "0")}:59
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Select a date and hour range to send the report with historical data for that window.
            </p>
            <Button
              className="w-full"
              style={{ backgroundColor: "#00A699" }}
              disabled={!hourlyOverrideDate || hourlyOverrideHourStart === "" || hourlyOverrideHourEnd === "" || triggering["hourly_delivery_report"]}
              onClick={handleHourlySendWithOverride}
            >
              {triggering["hourly_delivery_report"] ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Send className="w-4 h-4 mr-1" />
              )}
              Send Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </PharmeasyLayout>
  );
};

export default PharmeasyNotifications;
